import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AssetInformation, AssetsDataService, AssetType, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { convertEpochToDateString } from '@functions/functions';
import { UploaderComponent } from '@syncfusion/ej2-angular-inputs';
import { RichTextEditorComponent } from '@syncfusion/ej2-angular-richtexteditor';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FilesService } from '@services/files.service';
import { IAccount } from '@services/accounts.service';

/** AssetInformationDialogData */
export class AssetInformationDialogData {
	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** asset */
	public asset: LevelAsset;

	/** account */
	public account?: IAccount;
}

@Component({
	selector: 'app-asset-information',
	templateUrl: './asset-information.component.html',
	styleUrls: ['./asset-information.component.scss']
})
export class AssetInformationComponent implements OnInit, AfterViewInit, OnChanges {

	/** accountGUID */
	@Input() accountGUID: string;

	/** account */
	@Input() account?: IAccount;

	/** organisationUID */
	@Input() organisationUID: string;

	/** asset */
	@Input() asset: LevelAsset;

	/** assetTypes */
	public assetTypes: AssetType[] = [];

	/** assetTypeFields */
	public assetTypeFields: Object = { text: "Type", value: "AssetTypeID" };

	/** thisUser */
	public thisUser: WebAppUser;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** assetInformation */
	public assetInformation: AssetInformation[] = [];

	/** textInformation */
	public textInformation: AssetInformation[] = [];

	/** fileInformation */
	public fileInformation: AssetInformation[] = [];

	/** newInfoTitle */
	public newInfoTitle: string;

	/** newInfoContent */
	public newInfoContent: string;

	/** newInfoValid */
	public newInfoValid: boolean = false;

	/** upload */
	@ViewChild('upload') upload: UploaderComponent;

	/** upload */
	@ViewChild('richText') richText: RichTextEditorComponent;

	/** allowedExtentions */
	public allowedExtentions: string = ".jpeg,.jpg,.png,.pdf,.xls,.xlsx,.doc,.docx,.ppt.pptx";

	/** isDialog */
	public isDialog: boolean = false;

	/** firstAssetTypeValuePopulated */
	public firstAssetTypeValuePopulated: boolean = false;

	/** useAccordions */
	public useAccordions: boolean = false;

	/** canEdit */
	public canEdit: boolean = false;

	/**
	 * Constructor 
	 * @param auth auth 
	 * @param globalFunctions globalFunctions  
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param assetsDataService assetsDataService 
	 * @param document document 
	 */
	constructor(
		public auth: AuthService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		private filesService: FilesService,
		@Inject(DOCUMENT) public document: Document,
		@Optional() @Inject(MAT_DIALOG_DATA) public dialogData: AssetInformationDialogData,
		@Optional() public dialogRef?: MatDialogRef<AssetInformationComponent>
	) {
		/** Get required subscriptions */
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser]) => !!currentUser)
		).subscribe({
			next: ([currentUser]) => {
				this.getAssetTypes();
				this.thisUser = currentUser;
				if (dialogData) {
					this.isDialog = true;
					this.accountGUID = dialogData.accountGUID;
					this.organisationUID = dialogData.organisationUID;
					this.asset = dialogData.asset;
					this.account = dialogData.account;
				}
				this.canEdit = this.thisUser.AdminSite || (this.account?.Admin ?? false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * ngOnChanges
	 * @param changes changes 
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes["asset"] && !changes["asset"].firstChange) {
			this.getAssetInformation();
		}
	}

	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.getAssetInformation();
	}

	/**
	 * getAssetInformation
	 */
	getAssetInformation() {
		this.assetInformation = [];
		this.textInformation = [];
		this.fileInformation = [];

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);

		this.assetsDataService.getAssetInformation$(this.accountGUID, this.organisationUID, this.asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((information) => !!information),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (information) => {
				this.assetInformation = information;
				this.textInformation = this.assetInformation
					.filter((x: AssetInformation) => !x.IsFile && x.Content)
					.sort((a: AssetInformation, b: AssetInformation) => b.AddedAt - a.AddedAt);
				this.fileInformation = this.assetInformation
					.filter((x: AssetInformation) => x.IsFile && x.FileName)
					.sort((a: AssetInformation, b: AssetInformation) => b.AddedAt - a.AddedAt);
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.getAssetInformation$:", error);
				}
			},
		});
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * checkNewInfoValid
	 */
	checkNewInfoValid() {
		setTimeout(() => {
			if (this.newInfoContent == "<p><br/></p>") this.newInfoContent = "";
			this.newInfoValid =
				((this.newInfoTitle ? this.newInfoTitle.length : 0) > 0) &&
				((this.newInfoContent ? this.newInfoContent.length : 0) > 0 && !this.upload.filesData[0])
				|| this.upload.filesData[0] && (this.newInfoContent ? this.newInfoContent.length : 0) == 0;
		}, 0);
	}

	/**
	 * postNewInfo
	 */
	postNewInfo(isFile: boolean, fileName?: string) {
		if (!this.newInfoValid) {
			return;
		}
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (this.newInfoContent == "<p><br/></p>") this.newInfoContent = "";

		const newInfo: AssetInformation = {
			AssetInformationID: 0,
			AssetInformationGUID: "",
			AssetID: this.asset.AssetID,
			OrganisationUID: this.organisationUID,
			Title: this.newInfoTitle,
			IsFile: isFile,
			AddedAt: 0,
			AddedBy: 0,
			AddedByName: "",
			Content: this.newInfoContent ?? "",
			FileName: fileName ?? ""
		}

		const formFile = new FormData();
		if (isFile) {
			const file = this.upload.filesData[0].rawFile as File;
			if (file instanceof File == false) return;
			if (file.size == 0) return;
			formFile.append('file', file, file.name);
		}

		this.globalFunctions.displaySpinner(true);

		this.assetsDataService.postAssetInformation$(this.accountGUID, this.organisationUID, this.asset.AssetID, newInfo, formFile).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result[0].Result == "Error" ? result[0].Error : result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				const assetInformationUID: string = result[0].Data;
				if (isFile) {
					this.postFile(assetInformationUID, formFile);
				}
				this.clearNewInfo();
				this.getAssetInformation();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.postAssetInformation$:", error);
				}
			},
		});
	}

	/**
	 * submitNewInfo
	 */
	submitNewInfo() {
		if (!this.newInfoValid) return;
		if (!this.upload.filesData[0]) {
			this.postNewInfo(false);
		} else {
			this.uploadNewFile(this.upload.filesData[0].name);
		}
	}

	/**
	 * uploadNewFile
	 * @param fileName fileName  
	 */
	uploadNewFile(fileName: string) {
		this.postNewInfo(true, fileName);
	}

	/**
	 * uploadChange
	 * @param event event  
	 */
	uploadChange(event: any) {
		this.upload.filesData[0];
	}

	/**
	 * clearNewInfo
	 */
	clearNewInfo() {
		this.newInfoTitle = "";
		this.newInfoContent = "";
		this.upload.clearAll();
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}

	/**
	 * postFile
	 * @param assetInformationUID assetInformationUID  
	 * @param file file 
	 */
	postFile(assetInformationUID: string, file: FormData) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);

		this.filesService.postAssetInformationFile$(this.accountGUID, this.organisationUID, assetInformationUID, file).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: "File Uploaded",
					duration: 1000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.filesService.postAssetInformationFile$:", error);
				}
			},
		});
	}

	/**
	 * getFile
	 * @param assetInformationUID assetInformationUID 
	 */
	getFile(assetInformationUID: string, filename: string, download?: boolean) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);

		this.filesService.getAssetInformationFile$(this.accountGUID, this.organisationUID, assetInformationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (result) => {
				if (download) {
					this.downloadBlob(result, filename);
				} else if (result.type == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
					if (confirm("This file cannot be viewed in a browser, download it instead?")) {
						this.downloadBlob(result, filename);
					}
				} else {
					this.openBlob(result);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.filesService.postAssetInformationFile$:", error);
				}
			},
		});
	}

	/**
	 * downloadBlob
	 * @param blob blob 
	 * @param filename filename 
	 */
	downloadBlob(blob: Blob, filename: string) {
		const url = window.URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();

		window.URL.revokeObjectURL(url);
	}

	/**
	 * openBlob
	 * @param blob blob  
	 */
	openBlob(blob: Blob) {
		const url = window.URL.createObjectURL(blob);
		window.open(url);
	}

	/**
	 * deleteAssetInformation
	 * @param assetInformationGUID assetInformationGUID 
	 */
	deleteAssetInformation(info: AssetInformation, isFile?: boolean) {
		this.globalFunctions.UserConfirm$(`Delete Asset Information '${info.Title}'?`).subscribe(OKResult => {
			if (OKResult) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				this.globalFunctions.displaySpinner(true);

				this.assetsDataService.deleteAssetInformation$(this.accountGUID, this.organisationUID, info.AssetInformationGUID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((result) => !!result),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
						this.globalFunctions.displaySpinner(false);
					})
				).subscribe({
					next: (result) => {
						this.globalFunctions.DisplaySnackBar({
							message: result[0].Result == "Error" ? result[0].Error : result[0].Result.replace("OK - ", ""),
							duration: 2000,
							action: ""
						});
						if (isFile) {
							this.deleteFile(info.AssetInformationGUID);
						}
						this.getAssetInformation();
					},
					error: (error) => {
						if (error.status != 404) {
							console.error("this.assetsDataService.deleteAssetInformation$:", error);
						}
					},
				});
			}
		});
	}

	/**
	 * deleteFile
	 * @param assetInformationGUID assetInformationGUID 
	 */
	deleteFile(assetInformationGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);

		this.filesService.deleteAssetInformationFile$(this.accountGUID, this.organisationUID, assetInformationGUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result,
					duration: 1000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.filesService.deleteAssetInformationFile$:", error);
				}
			},
		});
	}

	/**
	 * getAssetTypes
	 */
	getAssetTypes() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getAssetTypes$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((types) => !!types),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (types) => {
				this.assetTypes = types;
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.getAssetTypes$:", error);
				}
			},
		});
	}

	/**
	 * patchAssetType
	 * @param event event 
	 */
	patchAssetType(event: any) {
		if (!this.firstAssetTypeValuePopulated) {
			this.firstAssetTypeValuePopulated = true;
			return;
		}
		this.asset.Type = event.itemData.AssetTypeID;

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.displaySpinner(true);

		this.assetsDataService.patchOrganisationAssets$(this.accountGUID, this.organisationUID, this.asset).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((types) => !!types),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (asset) => {
				this.globalFunctions.DisplaySnackBar({
					message: "Asset Type Updated",
					duration: 2000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.patchOrganisationAssets$:", error);
				}
			},
		});
	}
}
